import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';

export const PAGE_SIZES = [10, 15, 20, 25];
const DEFAULT_PAGE_SIZE = 10;

const readSize = (key) => {
  const v = parseInt(localStorage.getItem(`pg:${key}`), 10);
  return PAGE_SIZES.includes(v) ? v : DEFAULT_PAGE_SIZE;
};

/**
 * Client-side pagination over an already filtered/sorted array.
 * Page size is remembered per `storageKey`. Page resets to 1 whenever the
 * underlying list length or the page size changes.
 */
export const usePagination = (items, storageKey) => {
  const [pageSize, setPageSizeState] = useState(() => readSize(storageKey));
  const [page, setPage] = useState(1);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => { setPage(1); }, [total, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const setPageSize = (n) => {
    localStorage.setItem(`pg:${storageKey}`, String(n));
    setPageSizeState(n);
  };

  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);
  const bar = { total, page, pageSize, totalPages, onPageChange: setPage, onPageSizeChange: setPageSize, testIdPrefix: storageKey };
  return { page, setPage, pageSize, setPageSize, pageItems, total, totalPages, bar };
};

const pageNumbers = (page, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const set = new Set([1, totalPages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= totalPages));
  const out = [];
  let prev = 0;
  [...set].sort((a, b) => a - b).forEach((n) => {
    if (n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  });
  return out;
};

const NavBtn = ({ onClick, disabled, children, testId, label }) => (
  <button
    type="button"
    data-testid={testId}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className="h-7 w-7 inline-flex items-center justify-center rounded border border-gray-200 bg-white text-gray-600 hover:bg-ica-navy hover:text-white hover:border-ica-navy disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200 transition-colors"
  >
    {children}
  </button>
);

/** Pagination toolbar — spread `usePagination(...).bar` into it. */
export const PaginationBar = ({ total, page, pageSize, totalPages, onPageChange, onPageSizeChange, testIdPrefix = 'pg', position = 'bottom', noun = 'records', className = '' }) => {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const tid = (s) => `${testIdPrefix}-${position}-${s}`;
  return (
    <div
      data-testid={tid('bar')}
      className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs text-gray-600 bg-white ${position === 'top' ? 'border-b' : 'border-t'} border-gray-200 ${className}`}
    >
      <div className="flex items-center gap-3">
        <span data-testid={tid('range')}>
          Showing <b className="text-ica-navy">{from}–{to}</b> of <b className="text-ica-navy">{total}</b> {noun}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="hidden sm:inline">Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(parseInt(v, 10))}>
            <SelectTrigger data-testid={tid('size')} className="h-7 w-[64px] text-xs px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)} data-testid={tid(`size-${n}`)} className="text-xs">{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <NavBtn testId={tid('first')} label="First page" onClick={() => onPageChange(1)} disabled={page === 1}><ChevronsLeft className="w-3.5 h-3.5" /></NavBtn>
        <NavBtn testId={tid('prev')} label="Previous page" onClick={() => onPageChange(page - 1)} disabled={page === 1}><ChevronLeft className="w-3.5 h-3.5" /></NavBtn>
        {pageNumbers(page, totalPages).map((n, i) => (
          n === '…' ? (
            <span key={`e${i}`} className="px-1 text-gray-400">…</span>
          ) : (
            <button
              key={n}
              type="button"
              data-testid={tid(`page-${n}`)}
              onClick={() => onPageChange(n)}
              aria-current={n === page ? 'page' : undefined}
              className={`h-7 min-w-7 px-2 rounded text-xs font-medium transition-colors ${n === page ? 'bg-ica-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {n}
            </button>
          )
        ))}
        <NavBtn testId={tid('next')} label="Next page" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}><ChevronRight className="w-3.5 h-3.5" /></NavBtn>
        <NavBtn testId={tid('last')} label="Last page" onClick={() => onPageChange(totalPages)} disabled={page >= totalPages}><ChevronsRight className="w-3.5 h-3.5" /></NavBtn>
      </div>
    </div>
  );
};

export default PaginationBar;
